import { useState, useMemo, useCallback, useRef, useEffect } from 'preact/hooks';
import type { FilteredLocation } from '../hooks/useFilters';
import LocationCard from './LocationCard';
import Pagination from './Pagination';

const ITEMS_PER_PAGE = 8;

type ListViewProps = {
	locations: FilteredLocation[];
	onCardClick: ( originalIndex: number ) => void;
	selectedIndex: number | null;
};

/**
 * ListView component.
 * Renders location cards in a paginated grid.
 *
 * @param {Object}             props             - Component props.
 * @param {FilteredLocation[]} props.locations   - Filtered location tuples with original indices.
 * @param {Function}           props.onCardClick - Callback when a card is clicked (receives original location index).
 * @return {*} jsx
 */
export default function ListView( { locations, onCardClick, selectedIndex }: ListViewProps ) {
	const listRef = useRef< HTMLDivElement >( null );
	const [ currentPage, setCurrentPage ] = useState( 1 );

	// Reset to page 1 when the locations array changes (e.g., filters applied).
	useEffect( () => {
		setCurrentPage( 1 );
	}, [ locations ] );

	// Scroll the list container into view when the page changes.
	const handlePageChange = useCallback( ( page: number ) => {
		setCurrentPage( page );
		listRef.current?.scrollIntoView( { behavior: 'smooth', block: 'start' } );
	}, [] );

	// Sort locations alphabetically by title, preserving original indices for dialog lookup.
	const sorted = useMemo( () => {
		return [ ...locations ].sort( ( a, b ) =>
			a.location.title.localeCompare( b.location.title )
		);
	}, [ locations ] );

	const totalPages = Math.ceil( sorted.length / ITEMS_PER_PAGE );

	// Get the slice of sorted locations for the current page.
	const pagedLocations = useMemo( () => {
		const start = ( currentPage - 1 ) * ITEMS_PER_PAGE;
		return sorted.slice( start, start + ITEMS_PER_PAGE );
	}, [ sorted, currentPage ] );

	return (
		<section className="str-maps__list" ref={ listRef } id="str-maps-list" aria-label="Member organizations" tabIndex={ -1 }>
			<ul className="str-maps__list-grid">
				{ pagedLocations.map( ( { location, originalIndex } ) => (
					<LocationCard
						key={ location.id }
						location={ location }
						onClick={ () => onCardClick( originalIndex ) }
						isSelected={ originalIndex === selectedIndex }
					/>
				) ) }
			</ul>

			<Pagination
				currentPage={ currentPage }
				totalPages={ totalPages }
				onPageChange={ handlePageChange }
			/>
		</section>
	);
}
